// Chemin: C:\gestion-copro\src\pages\admin\CalendrierPage.tsx
// Module Calendrier / Agenda - Vue mensuelle avec CRUD
// Session 94 : Création initiale
// Droits : Coordinateur = CRUD complet, CS = lecture seule, Copro = événements publics

import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  Evenement,
  EvenementFormData,
  EVENEMENT_FORM_DEFAULTS,
  TypeEvenement,
  TYPE_EVENEMENT_LABELS,
  TYPE_EVENEMENT_COULEURS,
  RECURRENCE_LABELS,
  VISIBILITE_LABELS,
  SOUS_TYPE_GARANTIE_LABELS,
  SousTypeGarantie,
} from '../../types/evenement.types';
import {
  getEvenementsMois,
  createEvenement,
  updateEvenement,
  deleteEvenement,
  firestoreToForm,
  joursRestants,
  getCouleurUrgence,
  formaterDateEvenement,
  formaterDateCourte,
} from '../../services/evenement.service';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Edit3,
  Trash2,
  Clock,
  MapPin,
  Bell,
  Eye,
  AlertTriangle,
  Users,
  UserCheck,
  CalendarClock,
  ShieldAlert,
  FileSignature,
  Scale,
  Gavel,
  Wrench,
  Banknote,
  Loader2,
  Info,
  Shield,
} from 'lucide-react';

// ============================================
// CONSTANTES
// ============================================

const JOURS_SEMAINE = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS_NOMS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Map icônes par type (composants Lucide)
const ICONE_MAP: Record<TypeEvenement, React.ReactNode> = {
  ag: <Users className="w-3.5 h-3.5" />,
  reunion_cs: <UserCheck className="w-3.5 h-3.5" />,
  rdv: <CalendarClock className="w-3.5 h-3.5" />,
  echeance_garantie: <ShieldAlert className="w-3.5 h-3.5" />,
  echeance_contrat: <FileSignature className="w-3.5 h-3.5" />,
  echeance_juridique: <Scale className="w-3.5 h-3.5" />,
  audience: <Gavel className="w-3.5 h-3.5" />,
  travaux: <Wrench className="w-3.5 h-3.5" />,
  appel_fonds: <Banknote className="w-3.5 h-3.5" />,
  autre: <Calendar className="w-3.5 h-3.5" />,
};

// ============================================
// UTILITAIRES CALENDRIER
// ============================================

/**
 * Génère la grille du mois : tableau de semaines,
 * chaque semaine = 7 jours (lun-dim), avec jours du mois précédent/suivant
 */
const genererGrilleMois = (annee: number, mois: number): (number | null)[][] => {
  const premierJour = new Date(annee, mois, 1);
  const dernierJour = new Date(annee, mois + 1, 0);
  const nbJours = dernierJour.getDate();

  // Jour de la semaine du 1er (0=dim, 1=lun... → on convertit en lun=0)
  let jourDebut = premierJour.getDay() - 1;
  if (jourDebut < 0) jourDebut = 6; // Dimanche → index 6

  const grille: (number | null)[][] = [];
  let semaine: (number | null)[] = [];

  // Cases vides avant le 1er
  for (let i = 0; i < jourDebut; i++) {
    semaine.push(null);
  }

  // Jours du mois
  for (let jour = 1; jour <= nbJours; jour++) {
    semaine.push(jour);
    if (semaine.length === 7) {
      grille.push(semaine);
      semaine = [];
    }
  }

  // Compléter la dernière semaine
  if (semaine.length > 0) {
    while (semaine.length < 7) {
      semaine.push(null);
    }
    grille.push(semaine);
  }

  return grille;
};

// ============================================
// COMPOSANT : PASTILLE ÉVÉNEMENT DANS LE CALENDRIER
// ============================================

interface PastilleEvenementProps {
  evenement: Evenement;
  onClick: (evt: Evenement) => void;
}

const PastilleEvenement: React.FC<PastilleEvenementProps> = ({ evenement, onClick }) => {
  const couleur = TYPE_EVENEMENT_COULEURS[evenement.type] || 'bg-gray-500';
  const heure = formaterDateCourte(evenement.dateDebut);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick(evenement);
      }}
      className={`w-full text-left text-xs px-1.5 py-0.5 rounded ${couleur} text-white truncate hover:opacity-80 transition-opacity flex items-center gap-1`}
      title={evenement.titre}
    >
      {heure && <span className="font-medium">{heure}</span>}
      <span className="truncate">{evenement.titre}</span>
    </button>
  );
};

// ============================================
// COMPOSANT : MODALE DÉTAIL ÉVÉNEMENT
// ============================================

interface DetailEvenementModalProps {
  evenement: Evenement;
  isCoordinateur: boolean;
  onClose: () => void;
  onEdit: (evt: Evenement) => void;
  onDelete: (evt: Evenement) => void;
}

const DetailEvenementModal: React.FC<DetailEvenementModalProps> = ({
  evenement, isCoordinateur, onClose, onEdit, onDelete
}) => {
  const jours = joursRestants(evenement.dateDebut);
  const couleurJours = getCouleurUrgence(jours);
  const couleurType = TYPE_EVENEMENT_COULEURS[evenement.type] || 'bg-gray-500';
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
        {/* Header avec couleur du type */}
        <div className={`${couleurType} p-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3 text-white">
            {ICONE_MAP[evenement.type]}
            <div>
              <span className="text-xs font-medium opacity-90">
                {TYPE_EVENEMENT_LABELS[evenement.type]}
                {evenement.sousTypeGarantie && ` — ${SOUS_TYPE_GARANTIE_LABELS[evenement.sousTypeGarantie]}`}
              </span>
              <h2 className="font-semibold text-lg leading-tight">{evenement.titre}</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-5 space-y-4">
          {/* Date et compte à rebours */}
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-gray-800 font-medium">
                {formaterDateEvenement(evenement.dateDebut, evenement.dateFin || undefined)}
              </p>
              <p className={`text-sm font-medium ${couleurJours}`}>
                {jours > 0 ? `Dans ${jours} jour${jours > 1 ? 's' : ''}` :
                 jours === 0 ? "Aujourd'hui" :
                 `Il y a ${Math.abs(jours)} jour${Math.abs(jours) > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          {/* Description */}
          {evenement.description && (
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-gray-400 mt-0.5" />
              <p className="text-gray-600 text-sm whitespace-pre-wrap">{evenement.description}</p>
            </div>
          )}

          {/* Récurrence */}
          {evenement.recurrence !== 'aucune' && (
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <p className="text-gray-600 text-sm">
                Récurrence : {RECURRENCE_LABELS[evenement.recurrence]}
              </p>
            </div>
          )}

          {/* Visibilité */}
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-gray-400" />
            <p className="text-gray-600 text-sm">
              {VISIBILITE_LABELS[evenement.visibilite]}
            </p>
          </div>

          {/* Alertes configurées */}
          {evenement.alertes.length > 0 && (
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-gray-400" />
              <p className="text-gray-600 text-sm">
                Alertes : {evenement.alertes.map(a => `J-${a.joursAvant}`).join(', ')}
              </p>
            </div>
          )}
        </div>

        {/* Actions (coordinateur uniquement) */}
        {isCoordinateur && (
          <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
            {!confirmDelete ? (
              <>
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer
                </button>
                <button
                  onClick={() => onEdit(evenement)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Edit3 className="w-4 h-4" />
                  Modifier
                </button>
              </>
            ) : (
              <div className="w-full flex items-center justify-between">
                <p className="text-sm text-red-600 font-medium">Confirmer la suppression ?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => onDelete(evenement)}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT : FORMULAIRE ÉVÉNEMENT (Modale)
// ============================================

interface FormulaireEvenementProps {
  initial?: EvenementFormData;
  editMode: boolean;
  onSave: (data: EvenementFormData) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}

const FormulaireEvenement: React.FC<FormulaireEvenementProps> = ({
  initial, editMode, onSave, onClose, saving
}) => {
  const [form, setForm] = useState<EvenementFormData>(initial || EVENEMENT_FORM_DEFAULTS);
  const [erreur, setErreur] = useState('');

  const handleChange = (field: keyof EvenementFormData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErreur('');
  };

  const handleSubmit = async () => {
    // Validation
    if (!form.titre.trim()) {
      setErreur('Le titre est obligatoire');
      return;
    }
    if (!form.dateDebut) {
      setErreur('La date est obligatoire');
      return;
    }
    if (form.type === 'echeance_garantie' && !form.sousTypeGarantie) {
      setErreur('Le type de garantie est obligatoire');
      return;
    }
    await onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex items-center justify-between rounded-t-xl z-10">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {editMode ? 'Modifier l\'événement' : 'Nouvel événement'}
          </h2>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.titre}
              onChange={(e) => handleChange('titre', e.target.value)}
              placeholder="Ex: Assemblée Générale annuelle"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Type d'événement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              value={form.type}
              onChange={(e) => handleChange('type', e.target.value as TypeEvenement)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.entries(TYPE_EVENEMENT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Sous-type garantie (conditionnel) */}
          {form.type === 'echeance_garantie' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de garantie <span className="text-red-500">*</span>
              </label>
              <select
                value={form.sousTypeGarantie}
                onChange={(e) => handleChange('sousTypeGarantie', e.target.value as SousTypeGarantie)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Sélectionner --</option>
                {Object.entries(SOUS_TYPE_GARANTIE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Date et heure début */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date début <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.dateDebut}
                onChange={(e) => handleChange('dateDebut', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure <span className="text-gray-400">(optionnel)</span>
              </label>
              <input
                type="time"
                value={form.heureDebut}
                onChange={(e) => handleChange('heureDebut', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Date et heure fin (optionnel) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date fin <span className="text-gray-400">(optionnel)</span>
              </label>
              <input
                type="date"
                value={form.dateFin}
                onChange={(e) => handleChange('dateFin', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure fin
              </label>
              <input
                type="time"
                value={form.heureFin}
                onChange={(e) => handleChange('heureFin', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Détails, lieu, ordre du jour..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Récurrence et Visibilité */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Récurrence
              </label>
              <select
                value={form.recurrence}
                onChange={(e) => handleChange('recurrence', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(RECURRENCE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibilité
              </label>
              <select
                value={form.visibilite}
                onChange={(e) => handleChange('visibilite', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(VISIBILITE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Alertes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alertes email
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.alerteJ30}
                  onChange={(e) => handleChange('alerteJ30', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                J-30
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.alerteJ7}
                  onChange={(e) => handleChange('alerteJ7', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                J-7
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.alerteJ1}
                  onChange={(e) => handleChange('alerteJ1', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                J-1
              </label>
            </div>
          </div>

          {/* Erreur */}
          {erreur && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              <AlertTriangle className="w-4 h-4" />
              {erreur}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                {editMode ? 'Enregistrer' : 'Créer l\'événement'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT : LISTE ÉVÉNEMENTS DU JOUR (panneau latéral)
// ============================================

interface ListeJourProps {
  jour: number;
  mois: number;
  annee: number;
  evenements: Evenement[];
  onClickEvenement: (evt: Evenement) => void;
}

const ListeJour: React.FC<ListeJourProps> = ({ jour, mois, annee, evenements, onClickEvenement }) => {
  const dateStr = new Date(annee, mois, jour).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <h3 className="font-semibold text-gray-800 mb-3 capitalize">{dateStr}</h3>
      {evenements.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-4">Aucun événement ce jour</p>
      ) : (
        <div className="space-y-2">
          {evenements.map(evt => {
            const couleur = TYPE_EVENEMENT_COULEURS[evt.type] || 'bg-gray-500';
            const heure = formaterDateCourte(evt.dateDebut);
            return (
              <button
                key={evt.id}
                onClick={() => onClickEvenement(evt)}
                className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-8 rounded-full ${couleur}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">{evt.titre}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{TYPE_EVENEMENT_LABELS[evt.type]}</span>
                      {heure && (
                        <>
                          <span>·</span>
                          <span>{heure}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-gray-300">
                    {ICONE_MAP[evt.type]}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL : CalendrierPage
// ============================================

const CalendrierPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { copropriete } = useCopropriete();
  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // État navigation
  const today = new Date();
  const [annee, setAnnee] = useState(today.getFullYear());
  const [mois, setMois] = useState(today.getMonth());
  const [jourSelectionne, setJourSelectionne] = useState<number | null>(today.getDate());

  // État données
  const [evenements, setEvenements] = useState<Evenement[]>([]);
  const [loading, setLoading] = useState(true);

  // État modales
  const [showFormulaire, setShowFormulaire] = useState(false);
  const [showDetail, setShowDetail] = useState<Evenement | null>(null);
  const [editEvenement, setEditEvenement] = useState<Evenement | null>(null);
  const [saving, setSaving] = useState(false);

  // Droits : seul le coordinateur (gestionnaire / admin_saas / isGestionnaireSaas) peut créer/modifier/supprimer
  const isCoordinateur = userProfile?.role === 'gestionnaire' 
    || userProfile?.role === 'admin_saas' 
    || userProfile?.isGestionnaireSaas === true;

  // ============================================
  // CHARGEMENT DONNÉES
  // ============================================

  useEffect(() => {
    const charger = async () => {
      if (!coproprieteId) return;
      setLoading(true);
      try {
        const data = await getEvenementsMois(coproprieteId, annee, mois);
        setEvenements(data);
      } catch (error) {
        console.error('Erreur chargement événements:', error);
      } finally {
        setLoading(false);
      }
    };
    charger();
  }, [coproprieteId, annee, mois]);

  // ============================================
  // NAVIGATION MOIS
  // ============================================

  const moisPrecedent = () => {
    if (mois === 0) {
      setMois(11);
      setAnnee(a => a - 1);
    } else {
      setMois(m => m - 1);
    }
    setJourSelectionne(null);
  };

  const moisSuivant = () => {
    if (mois === 11) {
      setMois(0);
      setAnnee(a => a + 1);
    } else {
      setMois(m => m + 1);
    }
    setJourSelectionne(null);
  };

  const allerAujourdhui = () => {
    const now = new Date();
    setAnnee(now.getFullYear());
    setMois(now.getMonth());
    setJourSelectionne(now.getDate());
  };

  // ============================================
  // GRILLE ET ÉVÉNEMENTS PAR JOUR
  // ============================================

  const grille = useMemo(() => genererGrilleMois(annee, mois), [annee, mois]);

  // Map jour → événements
  const evenementsParJour = useMemo(() => {
    const map: Record<number, Evenement[]> = {};
    evenements.forEach(evt => {
      const jour = evt.dateDebut.toDate().getDate();
      if (!map[jour]) map[jour] = [];
      map[jour].push(evt);
    });
    return map;
  }, [evenements]);

  // Événements du jour sélectionné
  const evenementsJourSelectionne = jourSelectionne
    ? (evenementsParJour[jourSelectionne] || [])
    : [];

  // Est-ce aujourd'hui ?
  const estAujourdhui = (jour: number): boolean => {
    return jour === today.getDate() && mois === today.getMonth() && annee === today.getFullYear();
  };

  // ============================================
  // HANDLERS CRUD
  // ============================================

  const handleCreer = async (formData: EvenementFormData) => {
    if (!coproprieteId || !userProfile?.id) return;
    setSaving(true);
    try {
      await createEvenement(coproprieteId, formData, userProfile.id);
      // Recharger les événements du mois
      const data = await getEvenementsMois(coproprieteId, annee, mois);
      setEvenements(data);
      setShowFormulaire(false);
    } catch (error) {
      console.error('Erreur création événement:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleModifier = async (formData: EvenementFormData) => {
    if (!coproprieteId || !userProfile?.id || !editEvenement?.id) return;
    setSaving(true);
    try {
      await updateEvenement(coproprieteId, editEvenement.id, formData, userProfile.id);
      const data = await getEvenementsMois(coproprieteId, annee, mois);
      setEvenements(data);
      setEditEvenement(null);
      setShowDetail(null);
    } catch (error) {
      console.error('Erreur modification événement:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSupprimer = async (evt: Evenement) => {
    if (!coproprieteId || !evt.id) return;
    try {
      await deleteEvenement(coproprieteId, evt.id);
      const data = await getEvenementsMois(coproprieteId, annee, mois);
      setEvenements(data);
      setShowDetail(null);
    } catch (error) {
      console.error('Erreur suppression événement:', error);
    }
  };

  const handleClickEvenement = (evt: Evenement) => {
    setShowDetail(evt);
  };

  const handleEdit = (evt: Evenement) => {
    setEditEvenement(evt);
    setShowDetail(null);
  };

  // ============================================
  // NOUVEAU : Pré-sélectionner la date du jour cliqué
  // ============================================

  const handleNouvelEvenement = () => {
    setShowFormulaire(true);
  };

  // FormData initial pour création (avec date pré-remplie si jour sélectionné)
  const formInitialCreation: EvenementFormData = {
    ...EVENEMENT_FORM_DEFAULTS,
    dateDebut: jourSelectionne
      ? `${annee}-${String(mois + 1).padStart(2, '0')}-${String(jourSelectionne).padStart(2, '0')}`
      : '',
  };

  // ============================================
  // RENDER
  // ============================================

  // Empty state si pas de copropriété
  if (!coproprieteId) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Calendrier</h2>
        <p className="text-gray-500">Aucune copropriété associée à votre compte.</p>
      </div>
    );
  }

  return (
    <>
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Calendrier</h1>
          <p className="text-gray-500">
            Échéances, réunions et événements de la copropriété
          </p>
        </div>
        {isCoordinateur && (
          <button
            onClick={handleNouvelEvenement}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Nouvel événement
          </button>
        )}
      </div>

      {/* Contenu principal : Calendrier + panneau latéral */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* CALENDRIER MENSUEL (2/3 largeur) */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Navigation mois */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <button
              onClick={moisPrecedent}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {MOIS_NOMS[mois]} {annee}
              </h2>
              <button
                onClick={allerAujourdhui}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors font-medium"
              >
                Aujourd'hui
              </button>
            </div>
            <button
              onClick={moisSuivant}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Grille */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : (
            <div className="p-4">
              {/* En-tête jours */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {JOURS_SEMAINE.map(j => (
                  <div key={j} className="text-center text-xs font-semibold text-gray-400 py-2">
                    {j}
                  </div>
                ))}
              </div>

              {/* Semaines */}
              {grille.map((semaine, si) => (
                <div key={si} className="grid grid-cols-7 gap-1">
                  {semaine.map((jour, ji) => {
                    if (jour === null) {
                      return <div key={`vide-${ji}`} className="min-h-[80px] bg-gray-50 rounded" />;
                    }

                    const evtsJour = evenementsParJour[jour] || [];
                    const isToday = estAujourdhui(jour);
                    const isSelected = jour === jourSelectionne;

                    return (
                      <button
                        key={jour}
                        onClick={() => setJourSelectionne(jour)}
                        className={`min-h-[80px] p-1 rounded border text-left transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-300'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {/* Numéro du jour */}
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium inline-flex items-center justify-center w-7 h-7 rounded-full ${
                            isToday
                              ? 'bg-blue-600 text-white'
                              : isSelected
                                ? 'text-blue-700'
                                : 'text-gray-700'
                          }`}>
                            {jour}
                          </span>
                          {evtsJour.length > 0 && (
                            <span className="text-xs text-gray-400">{evtsJour.length}</span>
                          )}
                        </div>

                        {/* Pastilles événements (max 2 + "...") */}
                        <div className="space-y-0.5">
                          {evtsJour.slice(0, 2).map(evt => (
                            <PastilleEvenement
                              key={evt.id}
                              evenement={evt}
                              onClick={handleClickEvenement}
                            />
                          ))}
                          {evtsJour.length > 2 && (
                            <div className="text-xs text-gray-400 text-center">
                              +{evtsJour.length - 2} autre{evtsJour.length - 2 > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Légende */}
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <div className="flex flex-wrap gap-3">
              {(['ag', 'reunion_cs', 'echeance_garantie', 'echeance_contrat', 'echeance_juridique', 'rdv', 'travaux'] as TypeEvenement[]).map(type => (
                <div key={type} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded-full ${TYPE_EVENEMENT_COULEURS[type]}`} />
                  <span className="text-xs text-gray-500">{TYPE_EVENEMENT_LABELS[type]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PANNEAU LATÉRAL : Événements du jour (1/3 largeur) */}
        <div className="lg:col-span-1 space-y-4">
          {/* Liste du jour sélectionné */}
          {jourSelectionne ? (
            <ListeJour
              jour={jourSelectionne}
              mois={mois}
              annee={annee}
              evenements={evenementsJourSelectionne}
              onClickEvenement={handleClickEvenement}
            />
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-400 text-sm">Cliquez sur un jour pour voir ses événements</p>
            </div>
          )}

          {/* Prochaines échéances critiques du mois */}
          {evenements.filter(e => 
            ['echeance_garantie', 'echeance_contrat', 'echeance_juridique'].includes(e.type)
          ).length > 0 && (
            <div className="bg-gradient-to-b from-orange-50 to-white rounded-xl shadow-sm border border-orange-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-5 h-5 text-orange-500" />
                <h3 className="font-semibold text-gray-800 text-sm">Échéances critiques</h3>
              </div>
              <div className="space-y-2">
                {evenements
                  .filter(e => ['echeance_garantie', 'echeance_contrat', 'echeance_juridique'].includes(e.type))
                  .slice(0, 5)
                  .map(evt => {
                    const jours = joursRestants(evt.dateDebut);
                    const couleur = getCouleurUrgence(jours);
                    return (
                      <button
                        key={evt.id}
                        onClick={() => handleClickEvenement(evt)}
                        className="w-full text-left p-2 rounded-lg hover:bg-orange-50 transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-800 truncate">{evt.titre}</p>
                        <p className={`text-xs ${couleur}`}>
                          {jours > 0 ? `J-${jours}` : jours === 0 ? "Aujourd'hui" : `Dépassé (J+${Math.abs(jours)})`}
                        </p>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Info SENTINELLE */}
          <div className="bg-gradient-to-b from-blue-50 to-white rounded-xl shadow-sm border border-blue-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold text-gray-800 text-sm">SENTINELLE</h3>
            </div>
            <p className="text-xs text-gray-500">
              SENTINELLE utilise le calendrier pour ses alertes proactives. 
              Plus vous renseignez d'échéances, plus les suggestions seront pertinentes : 
              "L'AG est dans 19 jours, le syndic a-t-il envoyé la convocation ?"
            </p>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* MODALES                                      */}
      {/* ============================================ */}

      {/* Modale création */}
      {showFormulaire && (
        <FormulaireEvenement
          initial={formInitialCreation}
          editMode={false}
          onSave={handleCreer}
          onClose={() => setShowFormulaire(false)}
          saving={saving}
        />
      )}

      {/* Modale édition */}
      {editEvenement && (
        <FormulaireEvenement
          initial={firestoreToForm(editEvenement)}
          editMode={true}
          onSave={handleModifier}
          onClose={() => setEditEvenement(null)}
          saving={saving}
        />
      )}

      {/* Modale détail */}
      {showDetail && (
        <DetailEvenementModal
          evenement={showDetail}
          isCoordinateur={isCoordinateur}
          onClose={() => setShowDetail(null)}
          onEdit={handleEdit}
          onDelete={handleSupprimer}
        />
      )}
    </>
  );
};

export default CalendrierPage;
